// GOOP ZONE — Settings Screen
// Design: Chalk on blackboard — matches the rest of the game UI
// Full chalk aesthetic: no green terminal colors anywhere

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import * as Slider from '@radix-ui/react-slider';
import type { AudioSettings } from '../game/types';
import { getConnectedControllerName } from '../game/gamepadInput';
import {
  getParticleDensity, setParticleDensity, type ParticleDensity,
  getResolutionPreset, setResolutionPreset, type ResolutionPreset,
  getScreenShakeEnabled, setScreenShakeEnabled,
  getGlowEffectsEnabled, setGlowEffectsEnabled,
  getScreenFlashEnabled, setScreenFlashEnabled,
  getDynamicLightingEnabled, setDynamicLightingEnabled,
} from '../game/graphicsSettings';
import { describeAutoChoice } from '../game/autoResolution';

interface SettingsScreenProps {
  onClose: () => void;
  initialSettings?: Partial<AudioSettings>;
  onSettingsChange?: (settings: AudioSettings) => void;
  hudScale?: number;
  onHudScaleChange?: (scale: number) => void;
  // BETA 16.59 — Wave 4 graphics callbacks. Optional; if absent the
  // panel still mutates the live graphicsSettings module (visual effects
  // change in real-time) but persistence relies on the host calling
  // saveUserSettings on close.
  onGraphicsChange?: () => void;
}

const DEFAULT_SETTINGS: AudioSettings = {
  masterVolume: 0.7,
  musicVolume: 0.35,
  sfxVolume: 0.6,
};

const KEYBINDS = [
  { action: 'Move',             keys: 'W A S D / Arrows' },
  { action: 'Interact / Collect', keys: 'E' },
  { action: 'Pause',            keys: 'Escape' },
  { action: 'Toggle Minimap',   keys: 'M' },
  { action: 'Zoom In / Out',    keys: '+ / −  or  Scroll' },
  { action: 'Debug Overlay',    keys: 'F3' },
  { action: 'Restart',          keys: 'R (Win Screen)' },
];

// ── Chalk palette ─────────────────────────────────────────────────────────────
const C = {
  white:     'rgba(224, 230, 241, 0.92)',
  bright:    'rgba(242, 246, 252, 1.0)',
  dim:       'rgba(152, 162, 180, 0.60)',
  highlight: 'rgba(255, 170, 80, 0.95)',
  board:     'rgba(15, 17, 23, 0.0)',   // full-bleed now — surface is the backdrop
  border:    'rgba(125, 138, 162, 0.28)',
  track:     'rgba(125, 138, 162, 0.20)',
  thumb:     'rgba(255, 170, 80, 0.95)',
};

export default function SettingsScreen({
  onClose,
  initialSettings,
  onSettingsChange,
  hudScale,
  onHudScaleChange,
  onGraphicsChange,
}: SettingsScreenProps) {
  const [settings, setSettings] = useState<AudioSettings>({
    ...DEFAULT_SETTINGS,
    ...initialSettings,
  });
  const [activeTab, setActiveTab] = useState<'audio' | 'display' | 'controls'>('audio');

  // BETA 16.58 — poll connected gamepad name for the Controls tab.
  // BETA 16.73 — Per Dylan (ROG Ally X): "it does not see my xbox
  // controls." Settings panel now ALSO polls navigator.getGamepads()
  // directly (not just the cached _connectedName from gamepadInput),
  // because the cache can be stale if the controller was paired before
  // the gamepadconnected listener installed. 250ms tick is a balance
  // between responsiveness and CPU load while Settings is open. As soon
  // as the user wiggles a stick or presses a button, the pad becomes
  // visible and the banner flips to green.
  const [controllerName, setControllerName] = useState<string | null>(getConnectedControllerName());
  useEffect(() => {
    const tick = () => {
      const cached = getConnectedControllerName();
      if (cached) {
        setControllerName(cached);
        return;
      }
      // Fallback: poll navigator.getGamepads() directly for a freshly-pressed pad.
      try {
        const pads = (typeof navigator !== 'undefined' && navigator.getGamepads)
          ? navigator.getGamepads()
          : [];
        for (const p of pads) {
          if (p && p.connected) {
            setControllerName(p.id || 'Controller');
            return;
          }
        }
      } catch { /* ignore */ }
      setControllerName(null);
    };
    const id = setInterval(tick, 250);
    tick();
    return () => clearInterval(id);
  }, []);

  // BETA 16.59 — Wave 4: graphics state read from the live graphicsSettings
  // module. Mutations write back synchronously so visual changes apply
  // in real-time (no apply-and-close flow). The host's onGraphicsChange
  // callback gets pinged so it can persist via saveUserSettings.
  const [particleD, setParticleD] = useState<ParticleDensity>(getParticleDensity());
  const [resPreset, setResPreset] = useState<ResolutionPreset>(getResolutionPreset());
  const [shakeOn, setShakeOn] = useState<boolean>(getScreenShakeEnabled());
  const [glowOn, setGlowOn] = useState<boolean>(getGlowEffectsEnabled());
  const [flashOn, setFlashOn] = useState<boolean>(getScreenFlashEnabled());
  const [lightOn, setLightOn] = useState<boolean>(getDynamicLightingEnabled());
  const pingHost = () => onGraphicsChange?.();

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); }
    },
    [onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  // BETA 16.93 — Controller phase 3: register a back-only focus screen
  // for the settings overlay so B-button on gamepad closes it. Full
  // dpad nav of sliders + preset buttons requires per-element focus
  // targets — deferred. The mouse / touch path still works fine for
  // changing values.
  useEffect(() => {
    let cancelled = false;
    const setup = async () => {
      const focusMod = await import('../game/gamepadFocus');
      if (cancelled) return;
      focusMod.registerFocusScreen('settings_overlay', [], () => onClose(), 0);
      focusMod.setActiveScreen('settings_overlay');
    };
    setup();
    return () => {
      cancelled = true;
      import('../game/gamepadFocus').then(focusMod => {
        focusMod.unregisterFocusScreen('settings_overlay');
        if (focusMod.getActiveScreen() === 'settings_overlay') {
          focusMod.setActiveScreen(null);
        }
      });
    };
  }, [onClose]);

  const updateSetting = (key: keyof AudioSettings, value: number) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    onSettingsChange?.(next);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      {/* Dark backdrop */}
      <div className="absolute inset-0" style={{ background:
        'radial-gradient(ellipse 85% 60% at 50% 112%, rgba(255,110,20,0.14) 0%, transparent 60%),' +
        'radial-gradient(ellipse 50% 40% at 8% -5%, rgba(150,70,220,0.10), transparent 60%),' +
        'radial-gradient(ellipse 50% 40% at 94% -5%, rgba(150,70,220,0.08), transparent 60%),' +
        'linear-gradient(180deg, #16181f 0%, #101218 45%, #0a0b10 100%)' }} />
      <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/%3E%3CfeColorMatrix type=\'saturate\' values=\'0\'/%3E%3C/filter%3E%3Crect width=\'120\' height=\'120\' filter=\'url(%23n)\' opacity=\'0.55\'/%3E%3C/svg%3E")', opacity: 0.08, mixBlendMode: 'overlay', pointerEvents: 'none' }} />

      {/* Panel — chalkboard style.
          BETA 16.75 — Per Dylan (ROG Ally X): "settings is being cut off
          on top and bottom for being too bi. this all needs to fit
          correctly ina 16:9 screen." On a 720p ROG Ally screen the
          settings panel content (icon scale + particle density + 5
          resolution buttons + 3 effect toggles + APPLY) was taller than
          the viewport, especially with Edge's window title bar eating
          ~40px. Now: maxHeight 90vh + overflow:auto on the panel body
          so it scrolls internally instead of getting clipped. */}
      <motion.div
        className="relative z-10 w-full h-full"
        style={{ padding: '3.2% 5% 2.4%' }}
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      >
        <div
          style={{
            background: 'transparent',
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            height: '100%',
            fontFamily: "'Share Tech Mono', 'Courier New', monospace",
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 20px 12px',
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <span
              style={{
                fontFamily: "'Anton', 'Archivo Black', sans-serif",
                color: C.bright,
                fontSize: '2.4rem',
                letterSpacing: '0.25em',
                textShadow: '1px 1px 0 rgba(0,0,0,0.8)',
              }}
            >
              SETTINGS
            </span>
            <ChalkButton onClick={onClose} label="[ESC] CLOSE" small />
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
            {(['audio', 'display', 'controls'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: '0.78rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: activeTab === tab ? C.highlight : C.dim,
                  background: activeTab === tab ? 'rgba(222,229,240,0.06)' : 'transparent',
                  borderBottom: activeTab === tab ? `2px solid ${C.highlight}` : '2px solid transparent',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content — BETA 16.75: scrollable when content exceeds viewport. */}
          <div style={{ padding: '20px 24px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
            {activeTab === 'audio' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <ChalkSlider
                  label="MASTER VOLUME"
                  value={settings.masterVolume}
                  onChange={v => updateSetting('masterVolume', v)}
                />
                <ChalkSlider
                  label="MUSIC VOLUME"
                  value={settings.musicVolume}
                  onChange={v => updateSetting('musicVolume', v)}
                />
                <ChalkSlider
                  label="SFX VOLUME"
                  value={settings.sfxVolume}
                  onChange={v => updateSetting('sfxVolume', v)}
                />
                <div style={{ color: C.dim, fontSize: '0.7rem', textAlign: 'center', paddingTop: 4, letterSpacing: '0.1em' }}>
                  Audio powered by Web Audio API — no external files
                </div>
              </div>
            ) : activeTab === 'display' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <div style={{ color: C.white, fontSize: '0.78rem', letterSpacing: '0.15em', marginBottom: 10 }}>
                    ICON GRID SIZE
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <ChalkButton
                      onClick={() => onHudScaleChange?.(Math.max(0.75, Math.round(((hudScale ?? 1.0) - 0.25) * 100) / 100))}
                      label="[ − ]"
                      small
                    />
                    <span style={{ color: C.highlight, fontSize: '1.1rem', minWidth: '4rem', textAlign: 'center' }}>
                      {Math.round((hudScale ?? 1.0) * 100)}%
                    </span>
                    <ChalkButton
                      onClick={() => onHudScaleChange?.(Math.min(2.0, Math.round(((hudScale ?? 1.0) + 0.25) * 100) / 100))}
                      label="[ + ]"
                      small
                    />
                  </div>
                  <div style={{ color: C.dim, fontSize: '0.7rem', marginTop: 10, letterSpacing: '0.1em' }}>
                    Scales weapon, perk, and Buncha Bullshiiii icons. Range: 75% – 200%.
                  </div>
                </div>

                {/* BETA 16.59 — Wave 4: GRAPHICS QUALITY KNOBS. All update
                    the live graphicsSettings module synchronously so the
                    next frame already shows the change. Host saves via
                    onGraphicsChange ping. */}
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                  <div style={{ color: C.white, fontSize: '0.78rem', letterSpacing: '0.15em', marginBottom: 10 }}>
                    PARTICLE DENSITY
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['off', 'low', 'medium', 'high'] as const).map(d => (
                      <button
                        key={d}
                        onClick={() => { setParticleDensity(d); setParticleD(d); pingHost(); }}
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          fontFamily: "'Share Tech Mono', monospace",
                          fontSize: '0.72rem',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: particleD === d ? C.highlight : C.dim,
                          border: `1px solid ${particleD === d ? C.highlight : C.border}`,
                          background: particleD === d ? 'rgba(222,229,240,0.1)' : 'transparent',
                          cursor: 'pointer',
                          borderRadius: 2,
                        }}
                      >{d}</button>
                    ))}
                  </div>
                  <div style={{ color: C.dim, fontSize: '0.68rem', marginTop: 6, letterSpacing: '0.08em' }}>
                    Lower = better FPS on slow devices.
                  </div>
                </div>

                {/* BETA 16.64 — Resolution presets. Per Dylan: "1080p,
                    2k, 4k, 720p, and it auto chose based on internet
                    connection and detection on how poerful the device".
                    AUTO uses navigator.hardwareConcurrency + deviceMemory
                    + connection.effectiveType to pick a target — see
                    autoResolution.ts. */}
                <div>
                  <div style={{ color: C.white, fontSize: '0.78rem', letterSpacing: '0.15em', marginBottom: 10 }}>
                    RESOLUTION
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(['auto', '720p', '1080p', '1440p', 'native'] as const).map(p => (
                      <button
                        key={p}
                        onClick={() => { setResolutionPreset(p); setResPreset(p); pingHost(); }}
                        style={{
                          flex: '1 1 0',
                          minWidth: 64,
                          padding: '6px 4px',
                          fontFamily: "'Share Tech Mono', monospace",
                          fontSize: '0.72rem',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: resPreset === p ? C.highlight : C.dim,
                          border: `1px solid ${resPreset === p ? C.highlight : C.border}`,
                          background: resPreset === p ? 'rgba(222,229,240,0.1)' : 'transparent',
                          cursor: 'pointer',
                          borderRadius: 2,
                        }}
                      >{p}</button>
                    ))}
                  </div>
                  <div style={{ color: C.dim, fontSize: '0.68rem', marginTop: 6, letterSpacing: '0.08em' }}>
                    AUTO picks based on your device + connection. Lower = better FPS.
                    {resPreset === 'auto' && (
                      <div style={{ marginTop: 4, color: C.white, opacity: 0.65 }}>
                        Detected: {describeAutoChoice(window.innerWidth, window.innerHeight)}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ color: C.white, fontSize: '0.78rem', letterSpacing: '0.15em', marginBottom: 10 }}>
                    EFFECTS
                  </div>
                  <ToggleRow
                    label="Screen Shake"
                    on={shakeOn}
                    onClick={() => { const v = !shakeOn; setScreenShakeEnabled(v); setShakeOn(v); pingHost(); }}
                  />
                  <ToggleRow
                    label="Screen Flash (Last Stand / hit)"
                    on={flashOn}
                    onClick={() => { const v = !flashOn; setScreenFlashEnabled(v); setFlashOn(v); pingHost(); }}
                  />
                  <ToggleRow
                    label="Glow Effects (enemy throw glow, etc.)"
                    on={glowOn}
                    onClick={() => { const v = !glowOn; setGlowEffectsEnabled(v); setGlowOn(v); pingHost(); }}
                  />
                  <ToggleRow
                    label="🔦 Dynamic Lighting (experimental — moody dark + glow)"
                    on={lightOn}
                    onClick={() => { const v = !lightOn; setDynamicLightingEnabled(v); setLightOn(v); pingHost(); }}
                  />
                </div>
              </div>
            ) : (
              <div>
                {/* BETA 16.58 — controller status banner. Green if a pad is connected,
                    dim if not. Per Dylan: "i can play with a controller. nothing put
                    in and i dont see it in setings." */}
                <div
                  style={{
                    border: `1px solid ${controllerName ? 'rgba(120,255,160,0.6)' : 'rgba(255,170,60,0.6)'}`,
                    background: controllerName ? 'rgba(40,80,40,0.25)' : 'rgba(80,60,20,0.25)',
                    borderRadius: 3,
                    padding: '10px 12px',
                    marginBottom: 14,
                    fontSize: '0.78rem',
                    letterSpacing: '0.06em',
                    color: controllerName ? '#aaffaa' : '#ffd28c',
                    textAlign: 'center',
                  }}
                >
                  {controllerName ? (
                    <>🎮 CONTROLLER CONNECTED — {controllerName}</>
                  ) : (
                    <>
                      🎮 PRESS ANY CONTROLLER BUTTON
                      <div style={{ fontSize: '0.68rem', marginTop: 4, opacity: 0.85, letterSpacing: '0.04em' }}>
                        Browsers only show your gamepad after you press a button.
                        Wiggle a stick or tap A — banner flips green.
                      </div>
                    </>
                  )}
                </div>

                <div style={{ color: C.dim, fontSize: '0.72rem', marginBottom: 8, letterSpacing: '0.1em' }}>
                  KEYBOARD — DEFAULT KEYBINDS
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 2, marginBottom: 14 }}>
                  {KEYBINDS.map((kb, i) => (
                    <div
                      key={kb.action}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        borderBottom: i < KEYBINDS.length - 1 ? `1px solid ${C.border}` : 'none',
                        color: C.white,
                        fontSize: '0.78rem',
                        letterSpacing: '0.05em',
                      }}
                    >
                      <span>{kb.action}</span>
                      <span
                        style={{
                          color: C.highlight,
                          border: `1px solid ${C.border}`,
                          borderRadius: 2,
                          padding: '2px 8px',
                          fontSize: '0.68rem',
                          background: 'rgba(222,229,240,0.06)',
                          letterSpacing: '0.08em',
                        }}
                      >
                        {kb.keys}
                      </span>
                    </div>
                  ))}
                </div>

                {/* BETA 16.58 — controller mapping reference */}
                <div style={{ color: C.dim, fontSize: '0.72rem', marginBottom: 8, letterSpacing: '0.1em' }}>
                  CONTROLLER — STANDARD MAPPING
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}>
                  {[
                    { action: 'Move',     keys: 'Left Stick / D-Pad' },
                    { action: 'Interact', keys: 'A / Cross' },
                    { action: 'Interact / Open', keys: 'X / Square' },
                    { action: 'Reroll',   keys: 'Y / Triangle' },
                    { action: 'Pause',    keys: 'B / Circle / Start' },
                  ].map((kb, i, arr) => (
                    <div
                      key={kb.action}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
                        color: C.white,
                        fontSize: '0.78rem',
                        letterSpacing: '0.05em',
                      }}
                    >
                      <span>{kb.action}</span>
                      <span
                        style={{
                          color: C.highlight,
                          border: `1px solid ${C.border}`,
                          borderRadius: 2,
                          padding: '2px 8px',
                          fontSize: '0.68rem',
                          background: 'rgba(222,229,240,0.06)',
                          letterSpacing: '0.08em',
                        }}
                      >
                        {kb.keys}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'flex-end' }}>
            <ChalkButton onClick={onClose} label="APPLY & CLOSE" />
          </div>

          {/* Chalk corner marks */}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Chalk Button ──────────────────────────────────────────────────────────────
function ChalkButton({ onClick, label, small }: { onClick: () => void; label: string; small?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontFamily: "'Share Tech Mono', monospace",
        fontSize: small ? '0.72rem' : '0.82rem',
        letterSpacing: '0.15em',
        color: hov ? 'rgba(255,175,85,1.0)' : 'rgba(222,229,240,0.75)',
        background: hov ? 'rgba(222,229,240,0.10)' : 'transparent',
        border: `1px solid ${hov ? 'rgba(222,229,240,0.55)' : 'rgba(125,138,162,0.30)'}`,
        borderRadius: 2,
        padding: small ? '4px 10px' : '7px 20px',
        cursor: 'pointer',
        transition: 'all 0.12s',
        textShadow: hov ? '0 0 8px rgba(255,175,85,0.4)' : 'none',
      }}
    >
      {label}
    </button>
  );
}

// ── Chalk Slider ──────────────────────────────────────────────────────────────
function ChalkSlider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  const pct = Math.round(value * 100);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ color: C.white, fontSize: '0.78rem', letterSpacing: '0.15em' }}>{label}</span>
        <span style={{ color: C.highlight, fontSize: '0.85rem', minWidth: '2.5rem', textAlign: 'right' }}>{pct}%</span>
      </div>
      {/*
        BETA 15.84 — Per Dylan: "i have to click above the sliders on the
        setting." Root cause: this used Tailwind utility classes (h-5, h-1,
        w-4, h-4) which weren't reaching the production bundle, leaving the
        slider hit zone collapsed and visually offset from where the user
        sees the bar. Switched to explicit inline-style dimensions:
          Slider.Root = 28px tall hit zone (was h-5 = 20px, but fattening to
                        28 gives easier clicking + matches what the user
                        intuitively aims for)
          Slider.Track = 6px tall bar
          Slider.Thumb = 18×18 thumb
        All dimensions are guaranteed regardless of Tailwind config.
      */}
      <Slider.Root
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
          touchAction: 'none',
          width: '100%',
          height: 28,
          cursor: 'pointer',
        }}
        value={[value]}
        min={0}
        max={1}
        step={0.01}
        onValueChange={([v]) => onChange(v)}
      >
        <Slider.Track
          style={{
            position: 'relative',
            flexGrow: 1,
            borderRadius: 999,
            height: 6,
            background: C.track,
          }}
        >
          <Slider.Range
            style={{
              position: 'absolute',
              height: '100%',
              borderRadius: 999,
              background: C.white,
            }}
          />
        </Slider.Track>
        <Slider.Thumb
          style={{
            display: 'block',
            width: 18,
            height: 18,
            background: C.thumb,
            border: '2px solid rgba(9,11,15,0.9)',
            borderRadius: 2,
            boxShadow: '0 0 6px rgba(222,229,240,0.4)',
            cursor: 'pointer',
            outline: 'none',
          }}
          aria-label={label}
        />
      </Slider.Root>
    </div>
  );
}

// ── Chalk corner bracket decorations ─────────────────────────────────────────
function ChalkCorners() {
  const style = (pos: React.CSSProperties): React.CSSProperties => ({
    position: 'absolute',
    width: 12,
    height: 12,
    borderColor: 'rgba(125,138,162,0.50)',
    borderStyle: 'solid',
    ...pos,
  });
  return (
    <>
      <div style={style({ top: -1, left: -1, borderWidth: '2px 0 0 2px' })} />
      <div style={style({ top: -1, right: -1, borderWidth: '2px 2px 0 0' })} />
      <div style={style({ bottom: -1, left: -1, borderWidth: '0 0 2px 2px' })} />
      <div style={style({ bottom: -1, right: -1, borderWidth: '0 2px 2px 0' })} />
    </>
  );
}

// BETA 16.59 — Wave 4 toggle row (label + on/off pill).
function ToggleRow({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 4px',
        color: 'rgba(222,229,240,0.92)',
        fontSize: '0.78rem',
        letterSpacing: '0.05em',
        borderBottom: '1px solid rgba(125,138,162,0.18)',
      }}
    >
      <span>{label}</span>
      <button
        onClick={onClick}
        style={{
          padding: '4px 14px',
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: '0.72rem',
          fontWeight: 'bold',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: on ? '#aaffaa' : 'rgba(152,162,180,0.55)',
          border: `1px solid ${on ? 'rgba(170,255,170,0.6)' : 'rgba(125,138,162,0.45)'}`,
          background: on ? 'rgba(40,80,40,0.25)' : 'rgba(60,60,60,0.18)',
          cursor: 'pointer',
          borderRadius: 2,
          minWidth: 70,
        }}
      >
        {on ? 'ON' : 'OFF'}
      </button>
    </div>
  );
}
